import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core'
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
} from '@angular/forms'
import { IMultiSuggestionDropdownService } from './i-multi-suggestion-dropdown.service'
import { merge, Observable, Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators'
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap'

@Component({
    selector: 'multi-suggestion-dropdown',
    templateUrl: './multi-suggestion-dropdown.component.html',
    styleUrl: './multi-suggestion-dropdown.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSuggestionDropdownComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class MultiSuggestionDropdownComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) serviceInstance: IMultiSuggestionDropdownService
  @Input() validators: { fn: ValidatorFn; type: string; msg: string }[] = []

  @ViewChild('instance', { static: false }) instance: NgbTypeahead

  private _selection: string[] = []
  private _isDisabled: boolean = false

  public get selection(): string[] {
    return this._selection
  }

  public get isDisabled(): boolean {
    return this._isDisabled
  }

  public form: FormGroup<{ suggestion: FormControl<string | null> }>
  public suggestion: FormControl<string | null>

  public initControls(): void {
    this.suggestion = new FormControl<string | null>(
      { value: null, disabled: this._isDisabled },
      this.validators.map(v => v.fn),
    )
  }

  public initForm(): void {
    this.form = new FormGroup<{ suggestion: FormControl<string | null> }>({
      suggestion: this.suggestion,
    })
  }

  public activeSearch: boolean = false
  public failSearch: boolean = false
  public clicks$: Subject<string> = new Subject<string>()
  public focuses$: Subject<string> = new Subject<string>()

  public search = (text$: Observable<string>): Observable<string[]> => {
    const debouncedText$: Observable<string> = text$.pipe(debounceTime(500), distinctUntilChanged())
    const clicksWithClosedPopup$: Observable<string> = this.clicks$.pipe(
      filter(() => !this.instance.isPopupOpen()),
    )
    return merge(debouncedText$, this.focuses$, clicksWithClosedPopup$).pipe(
      switchMap(term => this.serviceInstance.getSuggestions(`${term}`)),
    )
  }

  public isFilled(): boolean {
    return this.suggestion.value !== null && this.suggestion.value.length > 0
  }

  public getErrorKeys(): string[] {
    if (this.suggestion && this.suggestion.errors) {
      return Object.keys(this.suggestion.errors)
    }
    return []
  }

  public getErrorMessageByType(type: string): string {
    const validator: { fn: ValidatorFn; type: string; msg: string }[] = this.validators.filter(
      e => e.type === type,
    )
    return validator.length > 0 ? validator[0].msg : 'Invalid input'
  }

  public reset(input: HTMLInputElement): void {
    this.suggestion.setValue(null)
    this.suggestion.markAsDirty()
    input.click()
  }

  public saveSuggestion(): void {
    if (this._isDisabled) {
      return
    }

    if (this.suggestion.value !== null && this.suggestion.value.length > 0) {
      const _val: string = this.suggestion.value
      if (this._selection.indexOf(_val) === -1) {
        this._selection.push(_val)
      }
      this.suggestion.setValue(null)
      this.onChange(JSON.stringify(this._selection))
    }
  }

  public removeSuggestion(index: number): void {
    if (this._isDisabled) {
      return
    }

    this._selection.splice(index, 1)
    this.onChange(JSON.stringify(this._selection))
  }

  public removeAllSuggestions(): void {
    if (this._isDisabled) {
      return
    }

    this._selection = []
    this.onChange(JSON.stringify(this._selection))
  }

  public onChange: any = (): void => {}

  public onTouched: any = (): void => {}

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  public setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled
    if (this._isDisabled) {
      this.form.get('suggestion').disable()
    } else {
      this.form.get('suggestion').enable()
    }
  }

  public writeValue(value: string | null): void {
    try {
      this._selection = JSON.parse(value) !== null ? JSON.parse(value) : []
    } catch (e) {
      this._selection = []
    }
  }

  public ngOnInit(): void {
    this.initControls()
    this.initForm()
  }
}
